import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  Divider,
  Fab,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import BackspaceIcon from "@mui/icons-material/Backspace";
import DeleteIcon from "@mui/icons-material/Delete";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import ScaleIcon from "@mui/icons-material/Scale";
import SearchIcon from "@mui/icons-material/Search";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import StorefrontIcon from "@mui/icons-material/Storefront";
import { Cliente } from "../models/ClienteModel";
import { LineaVenta } from "../models/LineaVentaModel";
import { Producto } from "../models/ProductoModel";
import { requestResultModel } from "../models/RequestReturnModel";
import { useAppConfig } from "../hooks/AppConfigContext";
import { useNotification } from "../hooks/NotificationContext";
import { useUserContext } from "../hooks/UserContext";
import { fetchGetClientes } from "../services/clienteService";
import { isAbortError } from "../services/fetchService";
import { fetchGuardarVenta } from "../services/ventaService";
import { parseDecimalValue } from "../utils/NumberParser";
import LocalSelector from "./components/LocalSelector";
import { useProductosVenta } from "../hooks/ProductosVentaContext";
import { CatalogoVentaActualizadoDialog } from "../components/CatalogoVentaActualizadoDialog";

type PresetRubro = "general" | "carniceria" | "panaderia";
type ModoCarga = "unidad" | "peso";

const presetLabels: Record<PresetRubro, string> = {
  general: "General",
  carniceria: "Carniceria",
  panaderia: "Panaderia",
};

const presetShortcuts: Record<PresetRubro, number[]> = {
  general: [1, 2, 3, 6],
  carniceria: [0.25, 0.5, 1, 2],
  panaderia: [0.5, 1, 6, 12],
};

const normalizar = (value: string | undefined) =>
  (value ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const currencyFormatter = new Intl.NumberFormat("es-AR", {
  style: "currency",
  currency: "ARS",
  minimumFractionDigits: 2,
});

const getProductoId = (producto: Producto) => {
  const item = producto as any;
  return Number(item.IDPRODUCTO ?? item.idProducto ?? item.IdProducto ?? item.id_producto ?? item.ID_PRODUCTO ?? 0);
};

const getProductoCodigo = (producto: Producto) => {
  const item = producto as any;
  return String(item.CODIGO ?? item.codigo ?? item.Codigo ?? item.cod_prod ?? item.Cod_Prod ?? "");
};

const getProductoNombre = (producto: Producto) => {
  const item = producto as any;
  return String(item.PRODUCTO ?? item.producto ?? item.Producto ?? "");
};

const getProductoNombreFontSize = (nombre: string) => {
  const limpio = nombre.trim();
  const palabras = limpio.split(/\s+/).filter(Boolean).length;
  const palabraLarga = limpio.split(/\s+/).some((palabra) => palabra.length >= 11);

  if (limpio.length > 70 || palabras >= 7) return "0.64rem";
  if (limpio.length > 54 || palabras >= 6) return "0.68rem";
  if (limpio.length > 40 || palabras >= 5 || palabraLarga) return "0.76rem";
  if (limpio.length > 26 || palabras >= 3) return "0.88rem";
  return "1.02rem";
};

const getClienteId = (cliente: Cliente | undefined) => {
  const item = cliente as any;
  return Number(item?.ID ?? item?.id ?? item?.Id ?? item?.idCliente ?? item?.IdCliente ?? item?.IDCLIENTE ?? 0);
};

const getClienteNombre = (cliente: Cliente | undefined) => {
  const item = cliente as any;
  return String(item?.CLIENTE ?? item?.cliente ?? item?.Cliente ?? item?.nombre ?? item?.Nombre ?? "");
};

const normalizarCliente = (cliente: Cliente): Cliente => ({
  ...cliente,
  ID: getClienteId(cliente),
  CLIENTE: getClienteNombre(cliente),
});

const normalizarClientesResponse = (data: unknown): Cliente[] => {
  const rawItems: unknown[] = Array.isArray(data)
    ? data
    : Array.isArray((data as any)?.data)
      ? (data as any).data
      : Array.isArray((data as any)?.Data)
        ? (data as any).Data
        : [];

  return rawItems
    .map((item) => normalizarCliente(item as Cliente))
    .filter((cliente) => getClienteId(cliente) > 0 && getClienteNombre(cliente));
};

const consumidorFinal: Cliente = {
  ID: 0,
  CLIENTE: "Consumidor final",
};

const getProductoPrecio = (producto: Producto) => {
  const item = producto as any;
  return parseDecimalValue(item.CONTADO ?? item.contado ?? item.Contado ?? item.precio ?? item.Precio ?? item.precioContado ?? item.PrecioContado ?? 0);
};

const getProductoRubro = (producto: Producto) => {
  const item = producto as any;
  return String(item.RUBRO ?? item.rubro ?? item.Rubro ?? "Sin rubro");
};

const normalizarProductoVenta = (producto: Producto): Producto => ({
  ...producto,
  IDPRODUCTO: getProductoId(producto),
  CODIGO: getProductoCodigo(producto),
  PRODUCTO: getProductoNombre(producto),
  CONTADO: getProductoPrecio(producto),
  RUBRO: getProductoRubro(producto),
});

const limitarDecimalesCantidad = (decimales: number | undefined) => Math.min(Math.max(Number(decimales ?? 2), 0), 6);

const redondearCantidad = (cantidad: number, decimales: number) => {
  const factor = Math.pow(10, decimales);
  return Math.round(cantidad * factor) / factor;
};

const getStepCantidad = (decimales: number) => decimales > 0 ? Number((1 / Math.pow(10, decimales)).toFixed(decimales)) : 1;

const formatearCantidad = (cantidad: number, decimales: number) =>
  cantidad.toLocaleString("es-AR", {
    minimumFractionDigits: cantidad % 1 === 0 ? 0 : decimales,
    maximumFractionDigits: decimales,
  });

const PuntoVentaRapido = () => {
  const { getUser, user } = useUserContext();
  const { config } = useAppConfig();
  const { showNotification } = useNotification();
  const userLoggedIn = useMemo(() => user ?? getUser(), [getUser, user]);
  const detalleVentaRef = useRef<HTMLDivElement | null>(null);
  const decimalesEnCantidad = limitarDecimalesCantidad(config?.decimalesEnCantidad);
  const stepCantidad = getStepCantidad(decimalesEnCantidad);

  const [idLocal, setIdLocal] = useState<string | undefined>(undefined);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [clienteSeleccionado, setClienteSeleccionado] = useState<Cliente | undefined>(undefined);
  const [lineas, setLineas] = useState<LineaVenta[]>([]);
  const [busqueda, setBusqueda] = useState("");
  const [rubroSeleccionado, setRubroSeleccionado] = useState("Todos");
  const [preset, setPreset] = useState<PresetRubro>("general");
  const [modoCarga, setModoCarga] = useState<ModoCarga>("unidad");
  const [cantidadRapida, setCantidadRapida] = useState(1);
  const { productos, loading: loadingProductos, revision } = useProductosVenta(idLocal);
  const [guardando, setGuardando] = useState(false);

  const cantidadRapidaOptions = useMemo(() => {
    const values = presetShortcuts[preset]
      .map((value) => redondearCantidad(value, decimalesEnCantidad))
      .filter((value) => value > 0);

    return Array.from(new Set(values.length > 0 ? values : [1]));
  }, [decimalesEnCantidad, preset]);

  const showMessage = useCallback((message: string, severity: "success" | "info" | "warning" | "error") => {
    showNotification(message, severity);
  }, [showNotification]);

  useEffect(() => {
    if (!userLoggedIn) return;

    const controller = new AbortController();
    fetchGetClientes(userLoggedIn, controller.signal)
      .then((data: unknown) => {
        const clientesNormalizados = normalizarClientesResponse(data);
        setClientes(clientesNormalizados);
        setClienteSeleccionado(consumidorFinal);
      })
      .catch((err) => {
        if (isAbortError(err)) return;
        showMessage("Error al cargar clientes", "error");
      });

    return () => controller.abort();
  }, [showMessage, userLoggedIn]);

  useEffect(() => {
    if (!cantidadRapidaOptions.includes(cantidadRapida)) {
      setCantidadRapida(cantidadRapidaOptions[0] ?? stepCantidad);
    }
  }, [cantidadRapida, cantidadRapidaOptions, stepCantidad]);

  const rubros = useMemo(() => {
    const values = productos.map(getProductoRubro).filter(Boolean);
    return ["Todos", ...Array.from(new Set(values)).sort()];
  }, [productos]);

  const productosFiltrados = useMemo(() => {
    const query = normalizar(busqueda);
    return productos
      .filter((producto) => rubroSeleccionado === "Todos" || getProductoRubro(producto) === rubroSeleccionado)
      .filter((producto) => {
        const searchable = normalizar(`${getProductoCodigo(producto)} ${getProductoNombre(producto)} ${getProductoRubro(producto)}`);
        return searchable.includes(query);
      })
      .slice(0, 48);
  }, [busqueda, productos, rubroSeleccionado]);

  const total = useMemo(() => {
    return lineas.reduce((acc, linea) => acc + getProductoPrecio(linea.producto) * linea.cantidad, 0);
  }, [lineas]);

  const cantidadEnVentaPorProducto = useMemo(() => {
    const map = new Map<number, number>();
    lineas.forEach((linea) => {
      const idProducto = getProductoId(linea.producto);
      if (!idProducto) return;
      map.set(idProducto, (map.get(idProducto) ?? 0) + linea.cantidad);
    });
    return map;
  }, [lineas]);

  const getCantidadEnVenta = useCallback((producto: Producto) =>
    cantidadEnVentaPorProducto.get(getProductoId(producto)) ?? 0,
    [cantidadEnVentaPorProducto]);

  const agregarProducto = (producto: Producto, cantidad = cantidadRapida) => {
    const productoNormalizado = normalizarProductoVenta(producto);
    const idProducto = getProductoId(productoNormalizado);
    const cantidadNormalizada = redondearCantidad(cantidad, decimalesEnCantidad);
    setLineas((actuales) => {
      const index = actuales.findIndex((linea) => getProductoId(linea.producto) === idProducto);
      if (index < 0) return [...actuales, { producto: productoNormalizado, cantidad: cantidadNormalizada }];

      return actuales.map((linea, idx) =>
        idx === index ? { ...linea, cantidad: redondearCantidad(linea.cantidad + cantidadNormalizada, decimalesEnCantidad) } : linea
      );
    });
  };

  const actualizarCantidad = (index: number, cantidad: number) => {
    if (cantidad <= 0) {
      quitarLinea(index);
      return;
    }

    setLineas((actuales) =>
      actuales.map((linea, idx) => (idx === index ? { ...linea, cantidad: redondearCantidad(cantidad, decimalesEnCantidad) } : linea))
    );
  };

  const quitarLinea = (index: number) => {
    setLineas((actuales) => actuales.filter((_, idx) => idx !== index));
  };

  const enviarVentaACaja = () => {
    if (!userLoggedIn || !idLocal || isNaN(Number(idLocal))) {
      showMessage("Seleccione local para enviar la venta", "warning");
      return;
    }

    if (lineas.length === 0) {
      showMessage("Agregue al menos un producto", "warning");
      return;
    }

    setGuardando(true);
    const idCliente = getClienteId(clienteSeleccionado);
    const clienteNombre = getClienteNombre(clienteSeleccionado) || consumidorFinal.CLIENTE;

    fetchGuardarVenta(userLoggedIn, idCliente, Number(idLocal), lineas, {
      numeroPv: Number((userLoggedIn as any).pv ?? (userLoggedIn as any).PV ?? (userLoggedIn as any).numeroPv ?? (userLoggedIn as any).NumeroPv ?? 0),
      idCaja: Number((userLoggedIn as any).idCaja ?? (userLoggedIn as any).IdCaja ?? 0),
      cliente: clienteNombre,
      total,
      descuento: 0,
      recargo: 0,
      observacion: "",
      ldp: "",
      origen: "PUNTO_VENTA_WEB",
      notificarWindowsForms: true,
    })
      .then((res: requestResultModel) => {
        showMessage(res.message, res.success ? "success" : "error");
        if (res.success) {
          setLineas([]);
          setBusqueda("");
        }
      })
      .catch((err) => {
        console.log(err);
        showMessage("Error al guardar la venta", "error");
      })
      .finally(() => setGuardando(false));
  };

  const aplicarCodigoRapido = () => {
    const query = normalizar(busqueda);
    if (!query) return;

    const producto = productos.find((item) => normalizar(getProductoCodigo(item)) === query);
    if (producto) {
      agregarProducto(producto);
      setBusqueda("");
    }
  };

  const irAlDetalleVenta = () => {
    detalleVentaRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, bgcolor: "#f7f7f4" }}>
      <CatalogoVentaActualizadoDialog productos={productos} revision={revision} lineas={lineas} setLineas={setLineas} />
      <Box
        sx={{
          alignItems: "flex-start",
          columnGap: 2,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) auto" },
          mb: 2,
          rowGap: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <PointOfSaleIcon color="primary" />
            <Typography
              variant="h4"
              sx={{
                fontSize: { xs: "2rem", md: "2.05rem", lg: "2.125rem" },
                fontWeight: 900,
                lineHeight: 1.15,
                overflowWrap: "normal",
                whiteSpace: { lg: "nowrap" },
              }}
            >
              Punto de venta rapido
            </Typography>
          </Stack>
          <Typography color="text.secondary" sx={{ maxWidth: { lg: 520 } }}>
            Carga agil por producto, codigo, rubro, unidad o peso.
          </Typography>
        </Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          sx={{ justifySelf: { lg: "end" }, width: { xs: "100%", lg: "auto" } }}
        >
          <LocalSelector
            onLocalChange={setIdLocal}
            size="small"
            sx={{ width: { xs: "100%", sm: 220 }, bgcolor: "white" }}
            selectSx={{ bgcolor: "white" }}
          />
          <TextField
            select
            SelectProps={{ native: true }}
            size="small"
            label="Cliente"
            value={clienteSeleccionado ? getClienteId(clienteSeleccionado) : consumidorFinal.ID}
            onChange={(event) => {
              const idCliente = Number(event.target.value);
              setClienteSeleccionado(idCliente === consumidorFinal.ID
                ? consumidorFinal
                : clientes.find((cliente) => getClienteId(cliente) === idCliente));
            }}
            sx={{ width: { xs: "100%", sm: 220 }, bgcolor: "white" }}
          >
            <option value={consumidorFinal.ID}>
              {consumidorFinal.CLIENTE}
            </option>
            {clientes.map((cliente) => (
              <option key={getClienteId(cliente)} value={getClienteId(cliente)}>
                {getClienteNombre(cliente)}
              </option>
            ))}
          </TextField>
        </Stack>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={8}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack spacing={2}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }}>
                <TextField
                  autoFocus
                  fullWidth
                  size="small"
                  placeholder="Buscar por codigo, producto o rubro"
                  value={busqueda}
                  onChange={(event) => setBusqueda(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") aplicarCodigoRapido();
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={preset}
                  onChange={(_, value) => value && setPreset(value)}
                  sx={{ bgcolor: "white" }}
                >
                  {(Object.keys(presetLabels) as PresetRubro[]).map((key) => (
                    <ToggleButton key={key} value={key}>
                      {presetLabels[key]}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={modoCarga}
                  onChange={(_, value) => value && setModoCarga(value)}
                  sx={{ bgcolor: "white" }}
                >
                  <ToggleButton value="unidad">
                    <ShoppingBasketIcon fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="peso">
                    <ScaleIcon fontSize="small" />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>

              <Stack direction="row" spacing={1} sx={{ overflowX: "auto", pb: 0.5 }}>
                {rubros.map((rubro) => (
                  <Chip
                    key={rubro}
                    label={rubro}
                    color={rubroSeleccionado === rubro ? "primary" : "default"}
                    onClick={() => setRubroSeleccionado(rubro)}
                    sx={{ fontWeight: rubroSeleccionado === rubro ? 800 : 500 }}
                  />
                ))}
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", rowGap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Cantidad rapida
                </Typography>
                <ButtonGroup size="small" variant="outlined">
                  {cantidadRapidaOptions.map((value) => (
                    <Button key={value} onClick={() => setCantidadRapida(value)} variant={cantidadRapida === value ? "contained" : "outlined"}>
                      {modoCarga === "peso" ? `${formatearCantidad(value, decimalesEnCantidad)} kg` : formatearCantidad(value, decimalesEnCantidad)}
                    </Button>
                  ))}
                </ButtonGroup>
                <TextField
                  size="small"
                  type="number"
                  value={cantidadRapida}
                  onChange={(event) => {
                    const nextCantidad = Number(event.target.value);
                    setCantidadRapida(Number.isFinite(nextCantidad) ? Math.max(nextCantidad, stepCantidad) : stepCantidad);
                  }}
                  inputProps={{ min: stepCantidad, step: modoCarga === "peso" ? stepCantidad : 1 }}
                  sx={{ width: 120 }}
                />
              </Stack>

              {!idLocal && (
                <Alert severity="info" icon={<StorefrontIcon />}>
                  Seleccione un local para cargar productos y comenzar la venta.
                </Alert>
              )}

              {loadingProductos ? (
                <Stack alignItems="center" sx={{ py: 8 }}>
                  <CircularProgress />
                </Stack>
              ) : (
                <Grid container spacing={1.5}>
                  {productosFiltrados.map((producto) => {
                    const cantidadEnVenta = getCantidadEnVenta(producto);
                    return (
                      <Grid item xs={6} sm={4} md={3} key={getProductoId(producto)}>
                        <Badge
                          badgeContent={formatearCantidad(cantidadEnVenta, decimalesEnCantidad)}
                          color="secondary"
                          invisible={cantidadEnVenta <= 0}
                          sx={{
                            width: "100%",
                            "& .MuiBadge-badge": {
                              fontWeight: 900,
                              minWidth: 24,
                              right: 8,
                              top: 8,
                            },
                          }}
                        >
                          <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => agregarProducto(producto)}
                            sx={{
                              alignItems: "stretch",
                              bgcolor: cantidadEnVenta > 0 ? "#fff8ed" : "white",
                              borderColor: cantidadEnVenta > 0 ? "secondary.main" : "#dedbd2",
                              color: "text.primary",
                              display: "flex",
                              height: { xs: 132, sm: 138 },
                              justifyContent: "space-between",
                              px: 1.15,
                              py: 1,
                              textAlign: "left",
                            }}
                          >
                            <Stack spacing={0.55} sx={{ width: "100%", minHeight: 0, minWidth: 0 }}>
                              <Typography
                                sx={{
                                  display: "-webkit-box",
                                  fontSize: getProductoNombreFontSize(getProductoNombre(producto)),
                                  fontWeight: 800,
                                  letterSpacing: 0,
                                  lineHeight: 1.08,
                                  minHeight: 0,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  WebkitBoxOrient: "vertical",
                                  WebkitLineClamp: 4,
                                  wordBreak: "break-word",
                                }}
                                title={getProductoNombre(producto)}
                              >
                                {getProductoNombre(producto)}
                              </Typography>
                              <Box sx={{ flexGrow: 1 }} />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: "block",
                                  fontSize: "0.72rem",
                                  lineHeight: 1.1,
                                  minHeight: 16,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                                title={getProductoCodigo(producto)}
                              >
                                {getProductoCodigo(producto)}
                              </Typography>
                              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ gap: 0.75, minHeight: 26 }}>
                                <Typography
                                  sx={{
                                    flex: "0 1 auto",
                                    fontSize: { xs: "0.82rem", sm: "0.88rem", xl: "0.94rem" },
                                    fontWeight: 900,
                                    minWidth: 0,
                                    overflow: "visible",
                                    textOverflow: "clip",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {currencyFormatter.format(getProductoPrecio(producto))}
                                </Typography>
                                <AddShoppingCartIcon color="primary" sx={{ flexShrink: 0, fontSize: 20 }} />
                              </Stack>
                            </Stack>
                          </Button>
                        </Badge>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={4} ref={detalleVentaRef}>
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              display: "flex",
              flexDirection: "column",
              height: { lg: "calc(100vh - 136px)" },
              maxHeight: { lg: "760px" },
              minHeight: { lg: 520 },
              overflow: "hidden",
              p: 2,
              position: { lg: "sticky" },
              top: 16,
            }}
          >
            <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ flexShrink: 0 }}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 900 }}>
                    Venta actual
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    {lineas.length} productos cargados
                  </Typography>
                </Box>
                <Tooltip title="Vaciar venta">
                  <span>
                    <IconButton color="error" disabled={lineas.length === 0} onClick={() => setLineas([])}>
                      <BackspaceIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>

              <Divider />

              <Stack
                spacing={1}
                sx={{
                  flex: { lg: 1 },
                  minHeight: 0,
                  overflowY: "auto",
                  overflowX: "hidden",
                  overscrollBehavior: "contain",
                  pr: 0.5,
                }}
              >
                {lineas.length === 0 && (
                  <Box sx={{ py: { xs: 4, lg: 2.5 }, textAlign: "center", color: "text.secondary" }}>
                    <ShoppingBasketIcon sx={{ fontSize: { xs: 42, lg: 34 }, mb: 0.75 }} />
                    <Typography>La venta esta vacia.</Typography>
                  </Box>
                )}
                {lineas.map((linea, index) => {
                  const precio = getProductoPrecio(linea.producto);
                  return (
                    <Box
                      key={`${getProductoId(linea.producto)}-${index}`}
                      sx={{
                        border: "1px solid #ece7e2",
                        borderRadius: 1,
                        minWidth: 0,
                        overflow: "visible",
                        p: 1,
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              display: "-webkit-box",
                              fontWeight: 800,
                              lineHeight: 1.2,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              WebkitBoxOrient: "vertical",
                              WebkitLineClamp: 3,
                              wordBreak: "break-word",
                            }}
                            title={getProductoNombre(linea.producto)}
                          >
                            {getProductoNombre(linea.producto)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {currencyFormatter.format(precio)} x {formatearCantidad(linea.cantidad, decimalesEnCantidad)}
                          </Typography>
                        </Box>
                        <IconButton size="small" color="error" onClick={() => quitarLinea(index)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                      <Box
                        sx={{
                          alignItems: "center",
                          columnGap: 1,
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) auto", lg: "1fr" },
                          mt: 1,
                          rowGap: 1,
                        }}
                      >
                        <ButtonGroup
                          size="small"
                          variant="outlined"
                          sx={{
                            justifySelf: "stretch",
                            minWidth: 0,
                            width: "100%",
                            "& .MuiButton-root": {
                              minWidth: 38,
                              width: 38,
                            },
                            "& .MuiInputBase-root": {
                              height: 38,
                            },
                          }}
                        >
                          <Button onClick={() => actualizarCantidad(index, linea.cantidad - cantidadRapida)}>
                            -
                          </Button>
                          <TextField
                            size="small"
                            type="number"
                            value={linea.cantidad}
                            onChange={(event) => actualizarCantidad(index, Number(event.target.value))}
                            inputProps={{
                              min: 0,
                              step: modoCarga === "peso" ? stepCantidad : 1,
                              style: { textAlign: "center" },
                            }}
                            sx={{
                              flex: "1 1 auto",
                              minWidth: 64,
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 0,
                              },
                              "& .MuiOutlinedInput-input": {
                                px: 0.5,
                              },
                            }}
                          />
                          <Button onClick={() => actualizarCantidad(index, linea.cantidad + cantidadRapida)}>
                            +
                          </Button>
                        </ButtonGroup>
                        <Typography
                          sx={{
                            fontSize: { xs: "1.1rem", xl: "1.2rem" },
                            fontWeight: 900,
                            justifySelf: "end",
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {currencyFormatter.format(precio * linea.cantidad)}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>

              <Divider />

              <Stack spacing={1.5} sx={{ flexShrink: 0 }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">Total</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 900 }}>
                    {currencyFormatter.format(total)}
                  </Typography>
                </Stack>
                <Button
                  size="large"
                  color="success"
                  variant="contained"
                  disabled={guardando || lineas.length === 0}
                  onClick={enviarVentaACaja}
                  startIcon={guardando ? <CircularProgress color="inherit" size={18} /> : <PointOfSaleIcon />}
                  sx={{ py: 1.3, fontWeight: 900 }}
                >
                  {guardando ? "Enviando..." : "Enviar venta a caja"}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
      <Fab
        color="primary"
        variant="extended"
        onClick={irAlDetalleVenta}
        sx={{
          bottom: 16,
          boxShadow: "0 12px 28px rgba(0,0,0,0.22)",
          display: { xs: "inline-flex", lg: "none" },
          fontWeight: 900,
          position: "fixed",
          right: 16,
          zIndex: 20,
        }}
      >
        <ShoppingBasketIcon sx={{ mr: 1 }} />
        {lineas.length > 0 ? `Detalle (${formatearCantidad(lineas.reduce((acc, linea) => acc + linea.cantidad, 0), decimalesEnCantidad)})` : "Detalle"}
      </Fab>
    </Box>
  );
};

export default PuntoVentaRapido;
